/**
 * Locale selection and the `t` accessor.
 *
 * The chosen locale is remembered in `localStorage`, because a Mini App is
 * relaunched constantly — closing and reopening the chat is a full page load —
 * and re-picking a language every time would be a small, permanent annoyance.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { languageHint } from '../telegram.ts';
import { catalogues, type Locale, type Messages } from './messages.ts';

const STORAGE_KEY = 'bingo.locale';

/**
 * Telegram's hint decides the *initial* language; an explicit choice overrides
 * it permanently. Note this reads `initDataUnsafe` — safe here, and only here,
 * because the worst a forged value can do is show the wrong words.
 */
const LOCALES: readonly Locale[] = ['en', 'am', 'aa', 'ti', 'so'];

function detectLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;

  const hint = (languageHint() ?? navigator.language)?.toLowerCase();
  const matched = hint ? LOCALES.find((l) => l !== 'en' && hint.startsWith(l)) : undefined;
  return matched ?? 'en';
}

function isLocale(value: string | null): value is Locale {
  return value !== null && (LOCALES as readonly string[]).includes(value);
}

interface I18nValue {
  locale: Locale;
  t: Messages;
  setLocale(locale: Locale): void;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next);
    // Ethiopic and Latin have different line-height needs, and `lang` is what
    // lets the stylesheet and the browser's own font fallback know which is on
    // screen.
    document.documentElement.lang = next;
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ locale, t: catalogues[locale], setLocale }),
    [locale, setLocale],
  );

  document.documentElement.lang = locale;

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n used outside I18nProvider');
  return value;
}

/** Shorthand for the common case of wanting only the messages. */
export function useT(): Messages {
  return useI18n().t;
}

export type { Locale, Messages };
export { LOCALES };
